import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { OidcOrgPublicClientMap } from '@netfoundry-ui/shared/model';
import { RecordDetailSection } from '@netfoundry-ui/ui/detail-drawer';
import { forgetFixedColumnWidths, LocalListTable, oidcListTabs } from '@netfoundry-ui/ui/table';
import { escape, orderBy } from 'lodash';
import { Subscription } from 'rxjs';
import { DataTableFilterService, ListColumn, ListSortDir } from 'ziti-console-lib';
import { OidcOrgPublicClientMapComponent } from './oidc-org-public-client-map.component';

const TABLE_ID = 'oidc-org-public-client-map-list';
const NAME_FILTER = 'name';
const DEFAULT_PAGE_SIZE = 30;

function formatDate(value: string | undefined): string {
    const date = value ? new Date(value) : undefined;
    return date && !isNaN(date.getTime()) ? date.toLocaleString() : value || '';
}

export type OidcOrgPublicClientMapRow = OidcOrgPublicClientMap & { actionList?: string[] };

@Component({
    selector: 'app-oidc-org-public-client-map-list',
    templateUrl: './oidc-org-public-client-map-list.component.html',
    styleUrls: ['./oidc-org-public-client-map-list.component.scss'],
    standalone: false,
})
export class OidcOrgPublicClientMapListComponent extends OidcOrgPublicClientMapComponent implements OnInit, OnDestroy {
    readonly tableId = TABLE_ID;
    readonly searchFilter = NAME_FILTER;
    readonly initialPageSize = DEFAULT_PAGE_SIZE;
    readonly tableOptions = { noSelect: true, pager: 'pages' as const };

    readonly tabs = oidcListTabs();

    listColumns: ListColumn<OidcOrgPublicClientMapRow>[] = [];
    readonly list = new LocalListTable<OidcOrgPublicClientMapRow>(
        inject(DataTableFilterService),
        TABLE_ID,
        DEFAULT_PAGE_SIZE,
        NAME_FILTER,
        () => this.refresh()
    );

    private readonly listSub = new Subscription();
    private listSort: { key: string; dir: ListSortDir } = { key: NAME_FILTER, dir: 'asc' };

    get accessDenied(): boolean {
        return !this.authorizationService.canListOIDCAudience();
    }

    override ngOnInit(): void {
        this.listColumns = this.buildColumns();
        forgetFixedColumnWidths(TABLE_ID, this.listColumns);
        this.listSub.add(this.list.connect());
        this.subscription.add(
            this.apiService.currentTenant.subscribe((currentTenant) => {
                if (currentTenant.id) {
                    this.tenantId = currentTenant.id;
                    this.isLoading = true;
                    this.handlePermissionCheck();
                }
            })
        );
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.listSub.unsubscribe();
    }

    override handlePermissionCheck(): void {
        super.handlePermissionCheck();
        if (this.accessDenied) {
            this.list.setItems([]);
        }
    }

    // The load ends here, so the rows go to the table instead of the old grid.
    override shouldShowNoData(): void {
        super.shouldShowNoData();
        if (!this.accessDenied) {
            this.list.setItems(this.sortedRows(this.oidcOrgPublicClientMaps as OidcOrgPublicClientMapRow[]));
        }
    }

    sortedRows(items: OidcOrgPublicClientMapRow[]): OidcOrgPublicClientMapRow[] {
        const { key, dir } = this.listSort;
        const value = (row: OidcOrgPublicClientMapRow) => String(row[key] ?? '').toLowerCase();
        return orderBy(items, [value], [dir]);
    }

    detailSections(row: OidcOrgPublicClientMapRow): RecordDetailSection[] {
        return [
            {
                title: 'Mapping',
                fields: [
                    { label: 'Name', value: row.name },
                    { label: 'OIDC Public Client Id', value: row.oidcPublicClientId, mono: true },
                    { label: 'Organization Id', value: row.organizationId, mono: true },
                    { label: 'Status', value: row.active ? 'Active' : 'Inactive' },
                ],
            },
            {
                title: 'Record',
                fields: [
                    { label: 'Id', value: row.id, mono: true },
                    { label: 'Created', value: formatDate(row.createdAt) },
                    { label: 'Created By', value: row.createdBy, mono: true },
                ],
            },
        ];
    }

    private buildColumns(): ListColumn<OidcOrgPublicClientMapRow>[] {
        const open = (ctx: { row: OidcOrgPublicClientMapRow }) => this.list.toggle(ctx.row);
        const onSort = (key: string, dir: ListSortDir) => {
            this.listSort = { key, dir: dir === 'desc' ? 'desc' : 'asc' };
            this.list.setItems(this.sortedRows(this.list.items));
        };
        const textColumn = (
            key: string,
            label: string,
            minWidth: number,
            extra = {}
        ): ListColumn<OidcOrgPublicClientMapRow> => ({
            key,
            label,
            minWidth,
            sortable: true,
            onSort,
            valueFormatter: (shim) => escape(String(shim.data[key] ?? '')),
            onCellClick: open,
            ...extra,
        });
        return [
            textColumn(NAME_FILTER, 'Name', 250, { sortDir: 'asc', hideable: false }),
            textColumn('id', 'Id', 300),
            textColumn('oidcPublicClientId', 'OIDC Public Client Id', 300),
            textColumn('organizationId', 'Organization Id', 300),
            {
                key: 'active',
                label: 'Active',
                width: 140,
                sortable: true,
                onSort,
                valueFormatter: (shim) =>
                    shim.data.active
                        ? '<span class="lt-status lt-status-online">Active</span>'
                        : '<span class="lt-status lt-status-offline">Inactive</span>',
                onCellClick: open,
            },
        ];
    }
}
